import React from "react";
import { cn } from "@/lib/utils";
import type { PreviewProps } from "./types";
import PropertyMap from "./PropertyMap";
import AddressSection from "./AddressSection";
import MarketPriceSection from "./MarketPriceSection";
import ValueDevelopmentSection from "./ValueDevelopmentSection";
import { Button } from "@/components/ui/button";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import PropertyDetails from "./PropertyDetails";
import { LockClosedIcon } from "@heroicons/react/24/solid";

/**
 * Preview Component - Displays property preview with map and details
 *
 * Shows a live preview of the property being evaluated, including:
 * - Interactive map with property location
 * - Property address
 * - Property details (rooms, size, etc.)
 * - Market price estimates (blurred)
 * - Value development chart (blurred)
 */
const Preview: React.FC<PreviewProps> = ({
  formData,
  coordinates,
  currentStepId,
  onStepClick,
  onReset,
  className,
}) => {
  const hasAddress = !!(formData.city || formData.street || formData.zip_code);

  const handleReset = () => {
    onReset?.();
  };

  return (
    <div className={cn("w-full flex flex-col", className)}>
      {/* Map Section - Always visible, no padding */}
      <div className="w-full h-64 shrink-0 overflow-hidden">
        <PropertyMap
          coordinates={coordinates}
          city={formData.city || formData.ort || ""}
        />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 relative">
        {/* Reset Button - Positioned top right under map */}
        <div className="absolute top-2 right-2 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleReset}
            className="h-8 w-8 bg-white/80 hover:bg-white shadow-sm border border-gray-200 rounded-full"
            title="Formular zurücksetzen"
          >
            <ArrowPathIcon className="h-4 w-4 text-gray-600" />
          </Button>
        </div>

        <div className="space-y-4 p-4 min-w-0">
          {/* Address Section */}
          {hasAddress && <AddressSection formData={formData} />}

          {/* Property Details Section */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3 font-serif">
              Immobiliendetails
            </h3>
            <PropertyDetails
              formData={formData}
              currentStepId={currentStepId}
              onStepClick={onStepClick}
            />
          </div>

          {/* Blurred Sections Container */}
          <div className="relative">
            {/* Overlay Mask */}
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center  backdrop-blur-[1px]">
              <div className="bg-white/20 backdrop-blur-sm p-4  w-full text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <LockClosedIcon className="w-5 h-5 text-gray-500" />
                </div>
                <p className="text-sm font-medium text-gray-900">
                  Füllen Sie die Bewertung aus, um weiteres zu sehen
                </p>
              </div>
            </div>

            {/* Market Price Section - Always visible but blurred via mask */}
            <div className="my-4">
              <MarketPriceSection />
            </div>

            {/* Value Development Section - Always visible but blurred via mask */}
            <div className="px-4">
              <ValueDevelopmentSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview;
